/**
 * 銀行CSVの読み取り。
 * 銀行ごとに列構成が違うため、ヘッダー名から自動判定しつつ、画面で列を選び直せるようにする。
 * 文字コードは UTF-8 と Shift_JIS の両方に対応する(国内の銀行CSVはSJISが多い)。
 */
import type { DateStr, Yen } from '@core/lib/types';

export type CsvTable = { headers: string[]; rows: string[][] };

export type ColumnMap = {
  date: number;
  description: number;
  /** 入金列・出金列がある形式 */
  amountIn: number | null;
  amountOut: number | null;
  /** 金額1列に符号で表す形式 */
  amountSigned: number | null;
};

export type ParsedRow = {
  lineNo: number;
  date: DateStr;
  description: string;
  direction: 'in' | 'out';
  amount: Yen;
};

export type ParseResult = {
  rows: ParsedRow[];
  /** 取り込めなかった行(日付や金額が読めない) */
  errors: { lineNo: number; reason: string }[];
};

/** UTF-8 で読めなければ Shift_JIS として読み直す */
export async function readCsvFile(file: File): Promise<string> {
  const buffer = await file.arrayBuffer();
  try {
    const text = new TextDecoder('utf-8', { fatal: true }).decode(buffer);
    return text.replace(/^﻿/, '');
  } catch {
    return new TextDecoder('shift_jis').decode(buffer);
  }
}

/** RFC4180 準拠の簡易パーサ(引用符・改行入りセル・CRLFに対応) */
export function parseCsv(text: string): CsvTable {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let inQuotes = false;

  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') {
          cell += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        cell += c;
      }
      continue;
    }
    if (c === '"') {
      inQuotes = true;
    } else if (c === ',') {
      row.push(cell);
      cell = '';
    } else if (c === '\n' || c === '\r') {
      if (c === '\r' && text[i + 1] === '\n') i++;
      row.push(cell);
      cell = '';
      if (row.some((v) => v.trim() !== '')) rows.push(row);
      row = [];
    } else {
      cell += c;
    }
  }
  row.push(cell);
  if (row.some((v) => v.trim() !== '')) rows.push(row);

  const headers = (rows.shift() ?? []).map((h) => h.trim());
  return { headers, rows };
}

const DATE_WORDS = ['日付', '取引日', '年月日', 'お取引日', '日時', 'date'];
const DESC_WORDS = ['摘要', '内容', 'お取引内容', '備考', '取引内容', 'description'];
const IN_WORDS = ['入金', 'お預入れ', '預入', '入金金額', 'お預かり金額'];
const OUT_WORDS = ['出金', 'お引出し', '引出', '支払金額', 'お支払金額', '出金金額'];
const AMOUNT_WORDS = ['金額', '取引金額'];

function findColumn(headers: string[], words: string[]): number | null {
  const index = headers.findIndex((h) =>
    words.some((w) => h.toLowerCase().replace(/\s/g, '').includes(w.toLowerCase())),
  );
  return index >= 0 ? index : null;
}

/** ヘッダー名から列を推定する。推定できない列は画面で選んでもらう */
export function detectColumns(headers: string[]): ColumnMap {
  const amountIn = findColumn(headers, IN_WORDS);
  const amountOut = findColumn(headers, OUT_WORDS);
  return {
    date: findColumn(headers, DATE_WORDS) ?? 0,
    description: findColumn(headers, DESC_WORDS) ?? 1,
    amountIn,
    amountOut,
    amountSigned: amountIn === null && amountOut === null ? findColumn(headers, AMOUNT_WORDS) : null,
  };
}

/** '2026/8/1' '2026-08-01' '2026年8月1日' '20260801' を 'YYYY-MM-DD' にする */
export function normalizeDate(raw: string): DateStr | null {
  const v = raw.trim();
  if (!v) return null;

  const compact = v.match(/^(\d{4})(\d{2})(\d{2})$/);
  if (compact) return `${compact[1]}-${compact[2]}-${compact[3]}`;

  const m = v.match(/^(\d{4})\s*[/\-年.]\s*(\d{1,2})\s*[/\-月.]\s*(\d{1,2})/);
  if (!m) return null;
  const [, y, mo, d] = m as unknown as [string, string, string, string];
  return `${y}-${mo.padStart(2, '0')}-${d.padStart(2, '0')}`;
}

/** '1,234' '¥1,234' '▲1,234' '(1,234)' '-1234' を整数にする(全角数字も可) */
export function normalizeAmount(raw: string): number | null {
  let v = raw
    .trim()
    .replace(/[０-９]/g, (c) => String.fromCharCode(c.charCodeAt(0) - 0xfee0))
    .replace(/[,，\s¥円]/g, '');
  if (!v) return null;

  let negative = false;
  if (/^[▲△−-]/.test(v)) {
    negative = true;
    v = v.slice(1);
  } else if (/^\(.*\)$/.test(v)) {
    negative = true;
    v = v.slice(1, -1);
  }
  if (!/^\d+$/.test(v)) return null;
  const n = Number(v);
  return negative ? -n : n;
}

/** 表と列対応から取込行を作る */
export function buildRows(table: CsvTable, map: ColumnMap): ParseResult {
  const rows: ParsedRow[] = [];
  const errors: { lineNo: number; reason: string }[] = [];

  table.rows.forEach((cells, i) => {
    const lineNo = i + 2; // ヘッダー行を1行目とする
    const date = normalizeDate(cells[map.date] ?? '');
    if (!date) {
      errors.push({ lineNo, reason: '日付を読み取れません' });
      return;
    }

    let direction: 'in' | 'out' | null = null;
    let amount: number | null = null;

    if (map.amountSigned !== null) {
      const signed = normalizeAmount(cells[map.amountSigned] ?? '');
      if (signed !== null && signed !== 0) {
        direction = signed > 0 ? 'in' : 'out';
        amount = Math.abs(signed);
      }
    } else {
      const inAmount = map.amountIn !== null ? normalizeAmount(cells[map.amountIn] ?? '') : null;
      const outAmount = map.amountOut !== null ? normalizeAmount(cells[map.amountOut] ?? '') : null;
      if (inAmount) {
        direction = 'in';
        amount = Math.abs(inAmount);
      } else if (outAmount) {
        direction = 'out';
        amount = Math.abs(outAmount);
      }
    }

    if (!direction || !amount) {
      errors.push({ lineNo, reason: '金額を読み取れません' });
      return;
    }

    rows.push({
      lineNo,
      date,
      description: (cells[map.description] ?? '').trim(),
      direction,
      amount,
    });
  });

  return { rows, errors };
}
